import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from '@/components/ui/form';
import { contactFormSchema, type ContactFormValues } from './lib/schema';
import { useAppSearchParams } from '@/hooks/use-app-search-params';
import { showToast } from '@/components/ui/show-toast';
import { SearchableTagInput } from '@/components/ui/searchable-tag-input';
import { useUpdateOrganisationMutation } from '@/services/organisations/queries';
import { useGetCountries } from '@/services/external/queries';
import type { Organisation } from '@/services/types';
import { cleanDeep } from '@/lib/utils';

interface ContactFormProps {
  data: Organisation | undefined;
  componentType?: 'verify' | 'edit';
}

export default function ContactForm({ data, componentType = 'verify' }: ContactFormProps) {
  const params = useAppSearchParams();
  
  const { data: countries = [], isLoading } = useGetCountries() 
  
  const countryOptions = useMemo(() => {
    return countries.map((country) => ({
      value: country.name,
      label: country.name,
      code: country.code,
      flag: country.flag
    }));
  }, [countries]);

  // Mutations
  const { mutate: updateOrganisation, isPending: isUpdatingOrganisation } = useUpdateOrganisationMutation(
    data?.id || '',
    () => {
      showToast('success', 'Contact information updated successfully');
      params.set({ view: 'expertise' });
    },
    (error) => {
      showToast('error', error?.response?.data?.message || 'Failed to update contact information. Please try again.');
    }
  );


  const extractPhoneData = (phoneNumber?: string) => {
    if (!phoneNumber) return { countryCode: 'NG', number: '' };

    const matchingCountry = countries.find((c) =>
      phoneNumber.startsWith(c.dial_code)
    );

    return {
      countryCode: matchingCountry?.code || 'NG',
      number: matchingCountry
        ? phoneNumber.substring(matchingCountry.dial_code.length)
        : phoneNumber
    };
  };

  const { countryCode, number } = extractPhoneData(data?.phoneNumber);

  const form = useForm<ContactFormValues>({
    resolver: zodResolver(contactFormSchema),
    defaultValues: {
      phoneCountry: countryCode,
      phoneNumber: number,
      headOffice: data?.headOfficeLocation || '',
      otherOffices: data?.officeLocations || [],
      paidStaffLocations: data?.paidStaffLocations || [],
      contactName: data?.contactPerson?.name || '',
      contactEmail: data?.contactPerson?.email || '',
      contactLinkedIn: data?.contactPerson?.linkedInUrl || '',
      contactBio: data?.contactPerson?.bio || ''
    }
  });

  useEffect(() => {
    if (countries.length > 0) {
      const { countryCode, number } = extractPhoneData(
        data?.phoneNumber
      );
      form.reset({
        phoneCountry: countryCode,
        phoneNumber: number,
        headOffice: data?.headOfficeLocation || '',
        otherOffices: data?.officeLocations || [],
        paidStaffLocations: data?.paidStaffLocations || [],
        contactName: data?.contactPerson?.name || '',
        contactEmail: data?.contactPerson?.email || '',
        contactLinkedIn: data?.contactPerson?.linkedInUrl || '',
        contactBio: data?.contactPerson?.bio || ''
      });
    }
  }, [data, countries]);

  const onSubmit = async (formData: ContactFormValues) => {
    if (!data?.id) {
      showToast('error', 'Organization data not available');
      return;
    }

    try {
      // Prepare organisation update data
      const updateData: any = {
        phoneNumber: formData.phoneNumber,
        headOfficeLocation: formData.headOffice,
        officeLocations: formData.otherOffices,
        paidStaffLocations: formData.paidStaffLocations,
        contactPerson: {
          name: formData.contactName,
          email: formData.contactEmail,
          bio: formData.contactBio,
          linkedInUrl: formData.contactLinkedIn
        }
      };

      // Update organisation
      updateOrganisation(cleanDeep(updateData));
    } catch (error) {
      showToast('error', 'Failed to update contact information. Please try again.');
    }
  };


  if (isLoading) {
    return <div className='flex justify-center p-6'>Loading countries...</div>;
  }

  return (
    <div className={`max-w-3xl p-6 ${componentType === 'verify' ? 'mx-auto' : ''}`}>
      <div className='space-y-8'>
        <div className='space-y-2'>
          <h1 className='font-grotesk text-2xl font-bold'>
            Contact Information
          </h1>
          <p className='text-[#545454]'>How can we get in touch?</p>
        </div>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className='space-y-6 font-grotesk'
          >
            {/* Phone Number */}
            <FormField
              control={form.control}
              name='phoneNumber'
              render={({ field }) => (
                <FormItem className='space-y-2'>
                  <FormLabel className='text-base font-medium text-[#111014]'>
                    Phone Number *
                  </FormLabel>
                  <div className='grid h-full w-full items-center rounded'>
                    <FormControl className='flex flex-1'>
                      <Input
                        {...field}
                        className='w-full flex-1 rounded-l-none bg-gray-50'
                        placeholder='Phone number'
                      />
                    </FormControl>
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Head Office Location - Now an Input */}
            <FormField
              control={form.control}
              name='headOffice'
              render={({ field }) => (
                <FormItem className='space-y-2'>
                  <FormLabel className='text-base font-medium text-[#111014]'>
                    Head Office Location
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder='Abuja, Nigeria'
                      className='bg-gray-50'
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Other Office Locations - Tag Selector */}
            {/* {renderTagSelector(
              'otherOffices',
              searchTerm,
              setSearchTerm,
              'Other Office Locations'
            )} */}

            {/* Locations with Paid Staff - Tag Selector */}
            {/* {renderTagSelector(
              'paidStaffLocations',
              searchTermPaidStaff,
              setSearchTermPaidStaff,
              'Locations
               with Paid Staff'
            )} */}

            <FormField
              control={form.control}
              name='otherOffices'
              render={({ field }) => (
                <FormItem>
                  <SearchableTagInput
                    label='Other Office Locations'
                    options={countryOptions.map((c) => ({
                      id: c.value,
                      label: c.label
                    }))}
                    selectedOptions={
                      field.value?.map((v) => ({
                        id: v,
                        label: v
                      })) || []
                    }
                    onChange={(options) =>
                      form.setValue(
                        'otherOffices',
                        options.map((o) => o.label)
                      )
                    }
                    placeholder='Search countries...'
                    className='w-full'
                  />
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Locations with Paid Staff */}
            <FormField
              control={form.control}
              name='paidStaffLocations'
              render={({ field }) => (
                <FormItem>
                  <SearchableTagInput
                    label='Locations with Paid Staff'
                    options={countryOptions.map((c) => ({
                      id: c.value,
                      label: c.label
                    }))}
                    selectedOptions={
                      field.value?.map((v) => ({
                        id: v,
                        label: v
                      })) || []
                    }
                    onChange={(options) =>
                      form.setValue(
                        'paidStaffLocations',
                        options.map((o) => o.label)
                      )
                    }
                    placeholder='Search countries...'
                    className='w-full'
                  />
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Contact Person's Name */}
            <FormField
              control={form.control}
              name='contactName'
              render={({ field }) => (
                <FormItem className='space-y-2'>
                  <FormLabel className='t ext-[#111014] font-medium'>
                    Contact Person&apos;s Name
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder='First name and Last name'
                      className='bg-gray-50'
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Contact Person's Email Address */}
            <FormField
              control={form.control}
              name='contactEmail'
              render={({ field }) => (
                <FormItem className='space-y-2'>
                  <FormLabel className='font-medium text-[#111014]'>
                    Contact Person&apos;s Email Address
                  </FormLabel>
                  <FormControl>
                    <Input {...field} type='email' className='bg-gray-50' />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Contact Person's LinkedIn URL */}
            <FormField
              control={form.control}
              name='contactLinkedIn'
              render={({ field }) => (
                <FormItem className='space-y-2'>
                  <FormLabel className='font-medium text-[#111014]'>
                    Contact Person&apos;s LinkedIn URL
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder='https://'
                      className='bg-gray-50'
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Contact Person's Bio */}
            <FormField
              control={form.control}
              name='contactBio'
              render={({ field }) => (
                <FormItem className='space-y-2'>
                  <FormLabel className='font-medium text-[#111014]'>
                    Contact Person&apos;s Bio
                  </FormLabel>
                  <p className='text-sm text-gray-500'>
                    Tell us about yourself!
                  </p>
                  <FormControl>
                    <Textarea
                      {...field}
                      placeholder='Bio'
                      className='min-h-[150px] resize-none bg-gray-50'
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Navigation Buttons */}
            <div className={`flex pt-4 ${componentType === 'edit' ? 'justify-end' : 'justify-between'}`}> 
              { componentType === 'verify' && <Button
                onClick={() => params.set({ view: 'company-info' })}
                type='button'
                variant='outline'
                className='w-[150px]'
              >
                Back
              </Button> }
              <Button
                type='submit'
                disabled={isUpdatingOrganisation}
                className='w-[150px]'
              >
                {isUpdatingOrganisation ? 'Saving...' : componentType === 'verify' ? 'Next' : 'Save'}
              </Button>
            </div>
          </form>
        </Form>
      </div>
    </div>
  );
}
